import { ErrorBox, Loader } from "@tfg/ui"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import type { LibraryResponses } from "@tfg/types/endpoints/library"

import { BlockedOnboardingRedirect } from "~/pages/SupplierPortal/Onboarding/components/BlockedOnboardingRedirect"
import { OnboardingGateRetryScreen } from "~/pages/SupplierPortal/Onboarding/components/OnboardingGateRetryBanner"
import {
  SUPPLIER_DEFAULT_HOME_PATH,
  SUPPLIER_ONBOARDING_ENTRY_PATH,
} from "~/pages/SupplierPortal/Onboarding/constants/onboardingGate.constants"
import { useOnboardingEntryDecision } from "~/pages/SupplierPortal/Onboarding/hooks/useOnboardingEntryDecision"
import { buildAuthenticatedNavigationTarget } from "~/pages/SupplierPortal/Onboarding/utils/buildAuthenticatedNavigationTarget"
import type { OnboardingEntryDecision } from "~/pages/SupplierPortal/Onboarding/utils/onboardingEntryDecision"
import {
  resolveGateState,
  type GateState,
} from "~/pages/SupplierPortal/Onboarding/utils/resolveGateState"
import { useAuth } from "~/store/auth-context"

export type { GateState }

type ReadyOnboardingGateProps = Readonly<{
  decision: OnboardingEntryDecision
  user: LibraryResponses.TFGUser
  getHomeRedirectUrl: (user: LibraryResponses.TFGUser) => string
  pathname: string
}>

const ReadyOnboardingGate = ({
  decision,
  user,
  getHomeRedirectUrl,
  pathname,
}: ReadyOnboardingGateProps) => {
  const isOnOnboardingRoute = pathname.startsWith(
    SUPPLIER_ONBOARDING_ENTRY_PATH
  )

  if (decision.outcome === "block") {
    return <BlockedOnboardingRedirect />
  }

  if (decision.outcome === "start" || decision.outcome === "resume") {
    if (!isOnOnboardingRoute) {
      const target = buildAuthenticatedNavigationTarget({
        decision,
        homeRedirectUrl: getHomeRedirectUrl(user),
      })

      return (
        <Navigate
          to={target.path}
          replace
          state={{ initialStepId: target.initialStepId }}
        />
      )
    }

    return <Outlet />
  }

  if (isOnOnboardingRoute) {
    return <Navigate to={SUPPLIER_DEFAULT_HOME_PATH} replace />
  }

  return <Outlet />
}

export const SupplierOnboardingGate = () => {
  const { t } = useTranslation("common")
  const { user, getHomeRedirectUrl } = useAuth()
  const location = useLocation()
  const gateQuery = useOnboardingEntryDecision(user)

  if (!user) {
    return <Outlet />
  }

  const state = resolveGateState(gateQuery)

  switch (state.status) {
    case "loading":
      return <Loader fullPage />
    case "authError":
      return <ErrorBox title={t("dataLoad.error.title")} error={state.error} />
    case "retryable":
      return <OnboardingGateRetryScreen onRetry={state.retry} />
    case "ready":
      return (
        <ReadyOnboardingGate
          decision={state.decision}
          user={user}
          getHomeRedirectUrl={getHomeRedirectUrl}
          pathname={location.pathname}
        />
      )
  }
}
